import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import MainLayout from '../../components/MainLayout';
import FactoryPageHeader from '../../components/factory/FactoryPageHeader';
import { fetchFactories, fetchFactoryDefectHistory } from '../../services/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useTranslation } from '../../context/LanguageContext';
import './FactoryDefectHistory.css';

/* ===== 공장 모니터링 > 불량 이력 =====
 * 공장 하나의 모든 사출기 불량을 기간 · 판정 기준(융합/비전/공정)으로 모아 본다. 사출기 현황과 같은 헤더·패널·셀렉트 스타일.
 * 구성: 툴바(기간·판정 기준·설비·유형) / KPI 5칸 / [설비 × 유형 표 | 시간대별 추이] / 불량 목록(행 → 사출기 대시보드 품질·생산).
 * 데이터: /dashboard/factory/{factoryId}/defect-history?from&to&source&equipmentId&type&page&size
 *   - KPI·설비×유형 표는 공장 전체, 추이·목록은 설비·유형 필터 적용.
 *   - 샷·판정 결과 보관이 2일이라 기간은 최근 2일 안에서만 고른다.
 * 스타일은 fd- 접두어, 색은 사출기 현황(fm-)과 같은 값. */

const PERIODS = ['today', 'yesterday', 'last24h', 'last48h'];
const SOURCES = ['fusion', 'vision', 'process'];
const PAGE_SIZE = 20;
const TYPE_COLORS = {
  FLASH: '#F59E0B', WELDLINE: '#A855F7', SHORT: '#EF4444', SINK: '#06B6D4', PROCESS: '#F97316', UNKNOWN: '#9CA3AF'
};
const VISION_IMAGE_VERSION = 2;   // 사출기 대시보드 비전검사와 같은 값 (nginx 1년 캐시 우회)

const pad2 = (n) => String(n).padStart(2, '0');
const isoLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
const num = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(d));

/* 기간 → [from, to) (브라우저 시각 = KST 기준) */
const periodRange = (period) => {
  const now = new Date();
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  if (period === 'yesterday') { const s = new Date(start); s.setDate(s.getDate() - 1); return [s, start]; }
  if (period === 'last24h') return [new Date(now.getTime() - 24 * 3600 * 1000), now];
  if (period === 'last48h') return [new Date(now.getTime() - 48 * 3600 * 1000), now];
  return [start, now];
};

/* 'YYYY-MM-DDTHH:mm:ss' → 'MM-DD HH:mm:ss' / 'HH시' */
const shortTime = (iso) => (iso ? String(iso).replace('T', ' ').slice(5, 19) : '—');
const bucketLabel = (iso, bucket) => {
  if (!iso) return '';
  const s = String(iso);
  return bucket === 'day' ? s.slice(5, 10) : `${s.slice(8, 10)}일 ${s.slice(11, 13)}시`;
};

function FactoryDefectHistory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const tr = useCallback((k, p) => t(`pages.overview.factoryDefect.${k}`, p), [t]);
  const typeLabel = useCallback((k) => tr(`types.${k || 'UNKNOWN'}`), [tr]);

  const [factories, setFactories] = useState([]);
  const [factoryId, setFactoryId] = useState(null);
  const [period, setPeriod] = useState('today');
  const [source, setSource] = useState('fusion');
  const [equipmentId, setEquipmentId] = useState(null);
  const [type, setType] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [syncTime, setSyncTime] = useState(null);
  const paramsRef = useRef({});

  /* 공장 목록 — URL factoryId 우선, 없으면 첫 공장 (사출기 현황과 같은 규칙) */
  useEffect(() => {
    let cancelled = false;
    fetchFactories()
      .then((list) => {
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        setFactories(arr);
        const fromUrl = parseInt(searchParams.get('factoryId'), 10);
        setFactoryId(arr.some((f) => f.id === fromUrl) ? fromUrl : (arr[0]?.id ?? null));
        if (arr.length === 0) setLoading(false);
      })
      .catch((err) => {
        console.error('공장 목록 조회 실패:', err);
        if (!cancelled) { setError(tr('factoryListLoadFailed')); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [searchParams, tr]);

  const load = useCallback(async (silent = false) => {
    const p = paramsRef.current;
    if (!p.factoryId) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const [from, to] = periodRange(p.period);
      const res = await fetchFactoryDefectHistory(p.factoryId, {
        from: isoLocal(from), to: isoLocal(to), source: p.source, equipmentId: p.equipmentId, type: p.type, page: p.page, size: PAGE_SIZE
      });
      setData(res || null);
      setSyncTime(new Date());
      setError(null);
    } catch (err) {
      console.error('불량 이력 조회 실패:', err);
      if (!silent) setError(tr('loadFailed'));
    } finally {
      if (silent) setTimeout(() => setRefreshing(false), 600); else setLoading(false);
    }
  }, [tr]);

  useEffect(() => {
    paramsRef.current = { factoryId, period, source, equipmentId, type, page };
    if (factoryId) load(false);
  }, [factoryId, period, source, equipmentId, type, page, load]);

  /* 지난 기간(어제)은 바뀌지 않으므로 자동 갱신하지 않는다 */
  useAutoRefresh(() => load(true), !!factoryId && period !== 'yesterday');

  const s = data?.summary;
  const types = useMemo(() => data?.types || [], [data]);
  const matrix = useMemo(() => data?.matrix || [], [data]);
  const items = data?.items || [];
  const totalItems = data?.totalItems || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const typeTotals = useMemo(() => {
    const out = {};
    types.forEach((k) => { out[k] = matrix.reduce((a, r) => a + (Number(r.byType?.[k]) || 0), 0); });
    return out;
  }, [types, matrix]);
  const cellMax = useMemo(
    () => Math.max(1, ...matrix.flatMap((r) => types.map((k) => Number(r.byType?.[k]) || 0))),
    [matrix, types]
  );
  const trendRows = useMemo(() => (data?.trend || []).map((p) => ({
    label: bucketLabel(p.bucket, data?.bucket), inspected: p.inspected, ...p.byType
  })), [data]);

  const resetPage = () => setPage(0);
  const changeSource = (v) => { setSource(v); setType(''); resetPage(); };
  const pickCell = (eqId, k) => {
    const same = equipmentId === eqId && type === (k || '');
    setEquipmentId(same ? null : eqId);
    setType(same ? '' : (k || ''));
    resetPage();
  };
  const openMachine = (it) => {
    const eq = matrix.find((r) => r.equipmentId === it.equipmentId);
    navigate('/machine', {
      state: {
        machineData: { id: it.equipmentId, name: eq?.equipmentName, equipmentName: eq?.equipmentName },
        openTab: source === 'vision' ? 'vision' : source === 'process' ? 'judge' : 'fusion'
      }
    });
  };

  const kpis = [
    { key: 'inspected', value: (s?.inspected ?? 0).toLocaleString() },
    { key: 'ng', value: (s?.ng ?? 0).toLocaleString(), tone: 'bad' },
    { key: 'ngRate', value: s?.ngRate != null ? `${Number(s.ngRate).toFixed(2)}%` : '—', tone: 'bad' },
    { key: 'topType', value: s?.topType ? typeLabel(s.topType) : '—', sub: s?.topType ? tr('countN', { n: s.topTypeCount }) : null, color: s?.topType ? TYPE_COLORS[s.topType] : null },
    { key: 'topEquipment', value: s?.topEquipmentName || '—', sub: s?.topEquipmentName ? tr('countN', { n: s.topEquipmentCount }) : null }
  ];

  const renderMatrix = () => (
    <div className="fd-matrix-wrap">
      <table className="fd-matrix">
        <thead>
          <tr>
            <th className="fd-mx-eq">{tr('colEquipment')}</th>
            {types.map((k) => <th key={k}><span className="fd-dot" style={{ background: TYPE_COLORS[k] }} />{typeLabel(k)}</th>)}
            <th>{tr('colTotal')}</th>
            <th>{tr('colRate')}</th>
          </tr>
        </thead>
        <tbody>
          {matrix.map((r) => {
            const noData = !r.inspected;
            return (
              <tr key={r.equipmentId} className={`${equipmentId === r.equipmentId ? 'active' : ''} ${noData ? 'fd-mx-nodata' : ''}`}>
                <td className="fd-mx-eq">
                  <button type="button" className="fd-mx-name" onClick={() => pickCell(r.equipmentId, '')}>{r.equipmentName}</button>
                </td>
                {types.map((k) => {
                  const n = Number(r.byType?.[k]) || 0;
                  const alpha = n > 0 ? 0.18 + 0.62 * (n / cellMax) : 0;
                  return (
                    <td key={k}>
                      <button
                        type="button"
                        className={`fd-mx-cell ${equipmentId === r.equipmentId && type === k ? 'active' : ''}`}
                        style={n > 0 ? { background: `${TYPE_COLORS[k]}${Math.round(alpha * 255).toString(16).padStart(2, '0')}` } : undefined}
                        onClick={() => pickCell(r.equipmentId, k)}
                        disabled={noData}
                      >
                        {noData ? '—' : n}
                      </button>
                    </td>
                  );
                })}
                <td className="fd-mx-total">{noData ? '—' : r.ng.toLocaleString()}</td>
                <td className="fd-mx-rate">{noData ? '—' : `${((r.ng * 100) / r.inspected).toFixed(2)}%`}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td className="fd-mx-eq">{tr('colTotal')}</td>
            {types.map((k) => <td key={k} className="fd-mx-total">{(typeTotals[k] || 0).toLocaleString()}</td>)}
            <td className="fd-mx-total">{(s?.ng ?? 0).toLocaleString()}</td>
            <td className="fd-mx-rate">{s?.ngRate != null ? `${Number(s.ngRate).toFixed(2)}%` : '—'}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );

  const renderTrend = () => (
    trendRows.length === 0 ? <div className="fd-message">{tr('noTrend')}</div> : (
      <div className="fd-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={trendRows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: '#98A8BF', fontSize: 12 }} axisLine={{ stroke: 'rgba(197,215,224,0.2)' }} tickLine={false} interval="preserveStartEnd" minTickGap={18} />
            <YAxis allowDecimals={false} tick={{ fill: '#98A8BF', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              contentStyle={{ background: '#111418', border: '1px solid rgba(197,215,224,0.2)', borderRadius: 6, color: '#FFFFFF', fontSize: 13 }}
              labelStyle={{ color: '#C5D7E0' }}
              formatter={(v, k) => [v, typeLabel(k)]}
            />
            {types.filter((k) => !type || k === type).map((k) => (
              <Bar key={k} dataKey={k} stackId="ng" fill={TYPE_COLORS[k]} isAnimationActive={false} maxBarSize={28} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    )
  );

  const renderList = () => (
    items.length === 0 ? <div className="fd-message">{s?.inspected ? tr('emptyFiltered') : tr('empty')}</div> : (
      <div className="fd-table-wrap">
        <table className="fd-table">
          <thead>
            <tr>
              <th>{tr('colTime')}</th>
              <th>{tr('colEquipment')}</th>
              <th>{tr('colMold')}</th>
              <th>{tr('colShot')}</th>
              <th>{tr('colType')}</th>
              {source !== 'vision' && <th>{tr('colProcess')}</th>}
              {source !== 'process' && <th>{tr('colVision')}</th>}
              {source === 'fusion' && <th>{tr('colFusion')}</th>}
              {source !== 'process' && <th>{tr('colImage')}</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={`${it.shotUuid}-${it.equipmentId}`} onClick={() => openMachine(it)} title={tr('rowHint')}>
                <td className="fd-num">{shortTime(it.shotTime)}</td>
                <td>{it.equipmentName || '—'}</td>
                <td>{it.craftId || '—'}</td>
                <td className="fd-num">#{it.shotCount ?? '—'}</td>
                <td><span className="fd-type-tag" style={{ background: TYPE_COLORS[it.defectType] || TYPE_COLORS.UNKNOWN }}>{typeLabel(it.defectType)}</span></td>
                {source !== 'vision' && <td className="fd-num">{num(it.processScore, 3)}</td>}
                {source !== 'process' && <td className="fd-num">{num(it.visionScore, 3)}</td>}
                {source === 'fusion' && <td className="fd-num fd-strong">{num(it.fusionScore, 3)}</td>}
                {source !== 'process' && (
                  <td className="fd-thumb-cell">
                    {it.imageFile ? <img className="fd-thumb" src={`/vision/${it.imageFile}?v=${VISION_IMAGE_VERSION}`} alt="" loading="lazy" /> : '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  );

  const filterLabel = equipmentId || type
    ? [equipmentId ? matrix.find((r) => r.equipmentId === equipmentId)?.equipmentName : null, type ? typeLabel(type) : null].filter(Boolean).join(' · ')
    : null;

  return (
    <MainLayout>
      <div className="fd-page">
        <FactoryPageHeader
          rootLabel={tr('breadcrumbRoot')}
          factories={factories}
          selectedFactoryId={factoryId}
          onSelectFactory={(id) => { setEquipmentId(null); setType(''); setPage(0); setFactoryId(id); }}
          pageLabel={tr('pageLabel')}
          syncTime={syncTime}
          onRefresh={() => load(true)}
          refreshTitle={tr('refresh')}
          refreshing={refreshing}
        />

        <section className="fd-panel fd-panel-top">
          <div className="fd-toolbar">
            <h2 className="fd-title">{tr('panelTitle', { n: s?.equipmentCount ?? matrix.length })}</h2>
            <label className="fd-field">
              <span>{tr('period')}</span>
              <select className="fd-select" value={period} onChange={(e) => { setPeriod(e.target.value); resetPage(); }}>
                {PERIODS.map((p) => <option key={p} value={p}>{tr(`periods.${p}`)}</option>)}
              </select>
            </label>
            <label className="fd-field">
              <span>{tr('source')}</span>
              <select className="fd-select" value={source} onChange={(e) => changeSource(e.target.value)}>
                {SOURCES.map((v) => <option key={v} value={v}>{tr(`sources.${v}`)}</option>)}
              </select>
            </label>
            <label className="fd-field">
              <span>{tr('equipment')}</span>
              <select className="fd-select" value={equipmentId ?? ''} onChange={(e) => { setEquipmentId(e.target.value ? Number(e.target.value) : null); resetPage(); }}>
                <option value="">{tr('all')}</option>
                {matrix.map((r) => <option key={r.equipmentId} value={r.equipmentId}>{r.equipmentName}</option>)}
              </select>
            </label>
            <label className="fd-field">
              <span>{tr('type')}</span>
              <select className="fd-select" value={type} onChange={(e) => { setType(e.target.value); resetPage(); }}>
                <option value="">{tr('all')}</option>
                {types.map((k) => <option key={k} value={k}>{typeLabel(k)}</option>)}
              </select>
            </label>
            <span className="fd-retention">{tr('retentionNote')}</span>
          </div>

          <div className="fd-kpis">
            {kpis.map((k) => (
              <div key={k.key} className={`fd-kpi ${k.tone ? `fd-kpi-${k.tone}` : ''}`}>
                <span className="fd-kpi-label">{tr(`kpi.${k.key}`)}</span>
                <span className="fd-kpi-value" style={k.color ? { color: k.color } : undefined}>{k.value}</span>
                {k.sub && <span className="fd-kpi-sub">{k.sub}</span>}
              </div>
            ))}
          </div>
        </section>

        {loading && !data ? (
          <section className="fd-panel fd-panel-fill"><div className="fd-message">{tr('loadingData')}</div></section>
        ) : error ? (
          <section className="fd-panel fd-panel-fill">
            <div className="fd-message fd-message-error">
              {error}
              <button type="button" className="fd-retry" onClick={() => load(false)}>{tr('refresh')}</button>
            </div>
          </section>
        ) : (
          <>
            <div className="fd-mid">
              <section className="fd-panel">
                <div className="fd-panel-head">
                  <h3 className="fd-panel-title">{tr('matrixTitle')}</h3>
                  <span className="fd-panel-sub">
                    {tr('matrixSub', { n: s?.equipmentWithData ?? 0, total: s?.equipmentCount ?? matrix.length })}
                  </span>
                </div>
                {renderMatrix()}
              </section>
              <section className="fd-panel">
                <div className="fd-panel-head">
                  <h3 className="fd-panel-title">{tr(data?.bucket === 'day' ? 'trendTitleDay' : 'trendTitleHour')}</h3>
                  <span className="fd-panel-sub">{filterLabel || tr('allEquipment')}</span>
                </div>
                {renderTrend()}
              </section>
            </div>

            <section className="fd-panel fd-panel-fill">
              <div className="fd-panel-head">
                <h3 className="fd-panel-title">
                  {tr('listTitle')}
                  <span className="fd-panel-count">{tr('countN', { n: totalItems.toLocaleString() })}</span>
                </h3>
                {filterLabel && (
                  <button type="button" className="fd-chip" onClick={() => { setEquipmentId(null); setType(''); resetPage(); }}>
                    {filterLabel}<span className="material-symbols-outlined">close</span>
                  </button>
                )}
              </div>
              {renderList()}
              {totalItems > PAGE_SIZE && (
                <div className="fd-pagination">
                  <button type="button" className="fd-page-nav" disabled={page === 0} onClick={() => setPage(page - 1)}>
                    <span className="material-symbols-outlined">chevron_left</span>{tr('prev')}
                  </button>
                  <span className="fd-page-info">{page + 1} / {totalPages}</span>
                  <button type="button" className="fd-page-nav" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                    {tr('next')}<span className="material-symbols-outlined">chevron_right</span>
                  </button>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </MainLayout>
  );
}

export default FactoryDefectHistory;
